import { Fragment, ReactElement, ReactNode } from 'react';

import { Tab as HeadlessTab } from '@headlessui/react';
import styled from 'styled-components';

import { Badge } from '@components/badge';
import { IconProps } from '@components/icons';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

export interface TabItem {
  icon: ReactElement<IconProps>;
  count?: number;
  active?: boolean;
  label: string;
  content: ReactNode;
  onClick?: () => void;
}

export interface TabProps {
  items: Array<TabItem>;
  selectedIndex?: number;
}

export function Tabs(props: TabProps) {
  const { items, selectedIndex } = props;

  return (
    <HeadlessTab.Group selectedIndex={selectedIndex} vertical>
      <Grid>
        <HeadlessTab.List as={TabContainer}>
          {items.map((item) => (
            <HeadlessTab key={item.label} as={Fragment}>
              <StyledTabItem onClick={item.onClick}>
                <Stack orientation="horizontal" space={2} align="center">
                  {item.icon}
                  <span>{item.label}</span>
                </Stack>
                {item.count !== undefined && (
                  <Badge variant={item.active ? 'active' : 'secondary'}>{item.count}</Badge>
                )}
              </StyledTabItem>
            </HeadlessTab>
          ))}
        </HeadlessTab.List>
        <HeadlessTab.Panels>
          {items.map((item) => (
            <HeadlessTab.Panel key={item.label}>{item.content}</HeadlessTab.Panel>
          ))}
        </HeadlessTab.Panels>
      </Grid>
    </HeadlessTab.Group>
  );
}

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  '> div:first-child': {
    gridColumnStart: 1,
    gridColumnEnd: 4,
  },
  '> div:last-child': {
    gridColumnStart: 4,
    gridColumnEnd: 10,
  },
  gap: pxToRem(24),
});

const TabContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  '[data-headlessui-focus-visible] &:focus-within': {
    borderRadius: pxToRem(4),
    boxShadow: theme.shadow.focus,
    '--shadow-focus-color': theme.color.elementBlue100,
    '--shadow-focus-gap': 0,
    '--shadow-focus-width': pxToRem(2),
  },
});

const StyledTabItem = styled.button({
  background: 'transparent',
  borderRadius: '0.25rem',
  color: theme.color.textPrimary,
  display: 'flex',
  flexdirection: 'row',
  justifyContent: 'space-between',
  outline: 'none',
  padding: '0.625rem 0.5rem',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: theme.color.uiTrans5,
  },

  '&[data-headlessui-state="selected"]': {
    backgroundColor: theme.color.uiTrans10,
    color: theme.color.elementBlue100,
  },
});
